import { Fragment } from 'react';
import type { Statement } from '@arcnet/contracts';
import { useSubroute } from '../../lib/subroute.js';
import { useStatement, useStatementMonths } from '../../lib/api/hooks-statements.js';
import { downloadFile } from '../../lib/api/download.js';
import { Badge, Empty, ErrorBanner } from '../../components/ui/Primitives.js';
import { toastError } from '../../components/ui/Toast.js';
import { formatMoney } from '../../lib/format.js';
import { docDate, printDocument } from './InvoiceDocument.js';

/**
 * Monthly statements (docs/28 B3): /my/billing/statements/2026-09, the latest month
 * when none is named. What was owed, invoiced, credited and paid in the month, what
 * was still unpaid at its end, and the airtime, for staff and the client alike.
 */

const monthName = (month: string): string =>
  new Intl.DateTimeFormat('en-ZA', { month: 'long', year: 'numeric', timeZone: 'UTC' })
    .format(new Date(`${month}-01T00:00:00Z`));

const MOVEMENT_LABEL: Record<string, string> = {
  topup: 'Airtime bought',
  call_charge: 'Calls',
  adjustment: 'Adjustments',
  invoice: 'Calls invoiced',
  refund: 'Refunds',
  monthly_fee: 'Monthly fees',
  reversal: 'Reversals',
};

const lines = (text: string | null | undefined): string[] =>
  (text ?? '').split(/\r?\n/).map((l) => l.trim()).filter(Boolean);

export function StatementsTab({ tenantId }: { tenantId: string | null }) {
  const route = useSubroute();
  const months = useStatementMonths(tenantId);
  const list = months.data?.data ?? [];
  const month = route.rest[0] ?? list[0] ?? null;
  const statement = useStatement(tenantId, month);

  if (months.error) return <ErrorBanner error={months.error} />;
  if (months.isLoading) {
    return <div className="arc-card arc-card-body"><div className="arc-skeleton" style={{ height: 240 }} /></div>;
  }
  if (!month) {
    return (
      <div className="arc-card">
        <Empty icon="billing" title="No statements yet"
          hint="A statement appears once the account has an invoice, a payment or airtime." />
      </div>
    );
  }

  const download = () => {
    void downloadFile(`/v1/tenants/${tenantId}/billing/statements/${month}/pdf`, `Statement ${month}.pdf`)
      .catch(toastError);
  };

  return (
    <>
      <div className="arc-row" style={{ gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <select className="arc-select" style={{ width: 'auto' }} aria-label="Month"
          value={month} onChange={(e) => route.go([e.target.value])}>
          {(list.includes(month) ? list : [month, ...list]).map((m) => (
            <option key={m} value={m}>{monthName(m)}</option>
          ))}
        </select>
        <div className="arc-spacer" />
        {statement.data && (
          <>
            <button type="button" className="arc-btn arc-btn-sm" onClick={download}>Download PDF</button>
            <button type="button" className="arc-btn arc-btn-sm" onClick={printDocument}>Print</button>
          </>
        )}
      </div>

      {statement.isLoading
        ? <div className="arc-card arc-card-body"><div className="arc-skeleton" style={{ height: 320 }} /></div>
        : statement.error
          ? <ErrorBanner error={statement.error} />
          : statement.data ? <StatementDocument statement={statement.data} /> : null}
    </>
  );
}

function StatementDocument({ statement: s }: { statement: Statement }) {
  const bank = s.issuer?.bank ?? null;

  return (
    <div className="arc-card arc-printable">
      <div className="arc-card-body">
        <div className="arc-row" style={{ alignItems: 'flex-start', gap: 16 }}>
          <div>
            <div className="arc-subtle" style={{ fontSize: 12.5 }}>{s.issuer?.name ?? s.customer.name}</div>
            <h2 className="arc-h2" style={{ marginTop: 2 }}>Statement</h2>
          </div>
          <div className="arc-spacer" />
          <div style={{ fontWeight: 600 }}>{monthName(s.month)}</div>
        </div>

        <div className="arc-grid arc-grid-2" style={{ marginTop: 16 }}>
          {s.issuer && (
            <div className="arc-doc-party">
              <div className="arc-label">From</div>
              <div style={{ fontWeight: 600 }}>{s.issuer.registered_name}</div>
              {lines(s.issuer.address).map((l, n) => <div key={n}>{l}</div>)}
              {s.issuer.vat_number && <div>VAT {s.issuer.vat_number}</div>}
            </div>
          )}
          <div className="arc-doc-party">
            <div className="arc-label">To</div>
            <div style={{ fontWeight: 600 }}>{s.customer.name}</div>
            {lines(s.customer.address).map((l, n) => <div key={n}>{l}</div>)}
            {s.customer.vat_number && <div>VAT {s.customer.vat_number}</div>}
          </div>
        </div>

        <dl className="arc-kv" style={{ marginTop: 16 }}>
          <dt>Owed on {docDate(s.period_start)}</dt>
          <dd className="arc-num">{formatMoney(s.opening, s.currency)}</dd>
          <dt>Owed on {docDate(s.period_end)}</dt>
          <dd className="arc-num" style={{ fontWeight: 600 }}>{formatMoney(s.closing, s.currency)}</dd>
        </dl>

        <div className="arc-label" style={{ margin: '18px 0 6px' }}>Account</div>
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Date</th><th>Description</th>
                <th className="arc-td-right">Amount</th>
                <th className="arc-td-right">Balance</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="arc-num arc-subtle">{docDate(s.period_start)}</td>
                <td style={{ fontWeight: 600 }}>Brought forward</td>
                <td />
                <td className="arc-td-right arc-num" style={{ fontWeight: 600 }}>{formatMoney(s.opening, s.currency)}</td>
              </tr>
              {s.lines.map((l, n) => (
                <tr key={`${l.kind}-${l.reference ?? ''}-${n}`}>
                  <td className="arc-num arc-subtle">{docDate(l.date)}</td>
                  <td>{l.description}</td>
                  <td className="arc-td-right arc-num">{formatMoney(l.amount, s.currency)}</td>
                  <td className="arc-td-right arc-num">{formatMoney(l.balance, s.currency)}</td>
                </tr>
              ))}
              <tr>
                <td className="arc-num arc-subtle">{docDate(s.period_end)}</td>
                <td style={{ fontWeight: 600 }}>Carried forward</td>
                <td />
                <td className="arc-td-right arc-num" style={{ fontWeight: 600 }}>{formatMoney(s.closing, s.currency)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {s.unpaid.length > 0 && (
          <>
            <div className="arc-label" style={{ margin: '18px 0 6px' }}>Unpaid on {docDate(s.period_end)}</div>
            <div className="arc-table-wrap">
              <table className="arc-table">
                <thead>
                  <tr>
                    <th>Invoice</th><th>Date</th><th>Due</th>
                    <th className="arc-td-right">Amount due</th>
                  </tr>
                </thead>
                <tbody>
                  {s.unpaid.map((u) => (
                    <tr key={u.number}>
                      <td className="arc-mono">{u.number}</td>
                      <td className="arc-num arc-subtle">{docDate(u.issue_date)}</td>
                      <td className="arc-num">
                        {docDate(u.due_date)}{u.overdue && <> <Badge tone="danger">overdue</Badge></>}
                      </td>
                      <td className="arc-td-right arc-num">{formatMoney(u.amount_due, s.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {s.airtime && (
          <>
            <div className="arc-label" style={{ margin: '18px 0 6px' }}>Airtime</div>
            <dl className="arc-kv">
              <dt>Balance on {docDate(s.period_start)}</dt>
              <dd className="arc-num">{formatMoney(s.airtime.opening, s.currency)}</dd>
              {s.airtime.movements.map((m) => (
                <Fragment key={m.kind}>
                  <dt>{MOVEMENT_LABEL[m.kind] ?? m.kind} ({m.count})</dt>
                  <dd className="arc-num">{formatMoney(m.amount, s.currency)}</dd>
                </Fragment>
              ))}
              <dt>Balance on {docDate(s.period_end)}</dt>
              <dd className="arc-num" style={{ fontWeight: 600 }}>{formatMoney(s.airtime.closing, s.currency)}</dd>
            </dl>
          </>
        )}

        {bank && Number(s.closing) > 0 && (
          <div className="arc-doc-note">
            Pay by EFT to {bank.account_name}, {bank.name}, account {bank.account_number},
            branch {bank.branch_code}, with the invoice number as the reference.
          </div>
        )}
      </div>
    </div>
  );
}
